import { useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/shared/ui/core/Button";

export type LoadMoreSentinelProps = {
  hasMore: boolean;
  onLoadMore: () => void;
  shown: number;
  /** Omit when total unknown (server infinite scroll). */
  total?: number;
  /** When true, auto-load when sentinel enters viewport (swipe/scroll). */
  infinite?: boolean;
  loading?: boolean;
  /** Optional custom message when all items are loaded */
  endMessage?: string;
};

/**
 * Mobile list foot — infinite scroll sentinel + fallback button.
 */
export function LoadMoreSentinel({
  hasMore,
  onLoadMore,
  shown,
  total,
  infinite = true,
  loading = false,
  endMessage,
}: LoadMoreSentinelProps) {
  const ref = useRef<HTMLDivElement>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  const lastTriggerRef = useRef(0);

  useEffect(() => {
    if (!infinite || !hasMore || loading) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          const now = Date.now();
          if (now - lastTriggerRef.current >= 400) {
            lastTriggerRef.current = now;
            onLoadMoreRef.current();
          }
        }
      },
      { root: null, rootMargin: "160px", threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [infinite, hasMore, loading, shown]);

  if (shown === 0) return null;

  if (!hasMore) {
    return (
      <p className="m-0 py-3 text-center text-xs text-text-muted select-none">
        {endMessage ?? (total != null ? `Đã hiển thị tất cả ${total} mục` : "Đã tải hết danh sách")}
      </p>
    );
  }

  if (infinite) {
    return (
      <div ref={ref} className="flex items-center justify-center py-3 select-none">
        {loading ? (
          <div className="flex items-center gap-2 text-xs font-medium text-text-muted">
            <Loader2 className="size-4 animate-spin text-primary" />
            <span>Đang tải thêm…</span>
          </div>
        ) : (
          <div className="h-4" />
        )}
      </div>
    );
  }

  const label =
    total != null
      ? `Xem thêm (${shown}/${total})`
      : `Xem thêm (${shown})`;

  return (
    <div ref={ref} className="py-2">
      <Button
        type="button"
        variant="secondary"
        size="md"
        className="w-full"
        disabled={loading}
        onClick={onLoadMore}
      >
        {loading ? "Đang tải…" : label}
      </Button>
    </div>
  );
}
